import React, { useState } from 'react'
import { FancyModal } from '@deal/components'
import { Redirect } from 'react-router-dom'
import {
  generateCreateUrlFromCategory,
  generateCreateUrl,
  getOnboardingPathSlug
} from '#src/services/path'
import { SellableBrandCategoryComponent } from '#src/generated/graphql'
import config from '#src/config'
import cookies from 'js-cookie'
import styles from './styles.css'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'

interface BrandCategoryModalProps {
  sellableId?: string
}

interface BrandCategoryModalContentProps {
  headline: React.ReactNode
  question: React.ReactNode
  onPathClick: () => void
  onDismissClick: () => void
}

const BrandCategoryModalContent: React.SFC<BrandCategoryModalContentProps> = ({
  headline,
  question,
  onPathClick,
  onDismissClick
}) => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.smallHeader}>{headline}</div>
      <div className={styles.largeHeader}>{question}</div>
      <div className={styles.buttonSet}>
        <button className={styles.button} onClick={onDismissClick}>
          Yes, I'm 100% sure of what I want
        </button>
        <button className={styles.button} onClick={onPathClick}>
          I think so, but it couldn't hurt to confirm
        </button>
        <button className={styles.button} onClick={onPathClick}>
          No, I'd love free expert advice
        </button>
      </div>
    </div>
  )
}

const BrandCategoryModal: React.SFC<BrandCategoryModalProps & WithAnalyticsProps> = ({
  sellableId,
  analytics
}) => {
  if (!sellableId) {
    return null
  }

  const [redirect, setRedirect] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(true)

  if (redirect) {
    return <Redirect to={redirect} push={true} />
  }

  const onDismissClick = () => {
    cookies.set('brand_category_modal_viewed', 'true', {
      expires: 1,
      domain: config.get('consumer.clientSideCookieDomain')
    })

    if (analytics) {
      analytics.brandCategoryModalDismissed()
    }

    setIsModalOpen(false)
  }

  if (cookies.get('brand_category_modal_viewed')) {
    return null
  }

  return (
    <FancyModal
      overlayClassName={styles.overlayClass}
      className={styles.modalClass}
      isOpen={isModalOpen}
      onRequestClose={onDismissClick}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <SellableBrandCategoryComponent
        variables={{
          friendlyIdOrId: sellableId
        }}
      >
        {({ data }) => {
          if (
            data &&
            data.findSellable &&
            data.findSellable.brand &&
            data.findSellable.categories.length
          ) {
            const category = data.findSellable.categories[0]
            const pathCategory = category.ancestorWithPath && category.ancestorWithPath.slug
            const pathUrl = pathCategory
              ? generateCreateUrlFromCategory(pathCategory)
              : generateCreateUrl(getOnboardingPathSlug())

            const brandName = data.findSellable.brand.displayName

            switch (category.slug) {
              case 'golf-clubs':
              case 'golf-bags':
              case 'golf-gloves':
              case 'golf-balls':
              case 'golf-rangefinders':
              case 'golf-tees':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about this {brandName}{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={onDismissClick}
                  />
                )

              case 'mountain-bikes':
              case 'electric-bikes':
              case 'bike-helmets':
              case 'bike-locks':
              case 'bike-pumps':
              case 'bike-tire-repair':
              case 'bike-mirrors':
              case 'bike-cleats':
              case 'bike-frames':
              case 'bike-pedals':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about this {brandName}{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={onDismissClick}
                  />
                )

              case 'skis':
              case 'ski-boots':
              case 'ski-bindings':
              case 'ski-poles':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about these {brandName}{' '}
                        <span className={styles.lowercase}>{category.pluralDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={onDismissClick}
                  />
                )

              case 'snowboards':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about this {brandName}{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={() => setIsModalOpen(false)}
                  />
                )

              case 'snowboard-boots':
              case 'snowboard-bindings':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about these {brandName}{' '}
                        <span className={styles.lowercase}>{category.pluralDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={() => setIsModalOpen(false)}
                  />
                )

              case 'winter-sports-jackets':
              case 'winter-sports-gloves':
              case 'winter-sports-pants':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about this {brandName}{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={() => setIsModalOpen(false)}
                  />
                )

              case 'wedding-rings':
              case 'engagement-rings':
              case 'diamonds':
                return (
                  <BrandCategoryModalContent
                    headline={
                      <>
                        Get free{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>{' '}
                        recommendations from an expert.
                      </>
                    }
                    question={
                      <>
                        Are you sure about this{' '}
                        <span className={styles.lowercase}>{category.singularDisplayName}</span>?
                      </>
                    }
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={() => setIsModalOpen(false)}
                  />
                )

              default:
                return (
                  <BrandCategoryModalContent
                    headline={'Get free recommendations from an expert.'}
                    question={'Are you sure about this product'}
                    onPathClick={() => setRedirect(pathUrl)}
                    onDismissClick={() => setIsModalOpen(false)}
                  />
                )
            }
          }

          return null
        }}
      </SellableBrandCategoryComponent>
    </FancyModal>
  )
}

export default withAnalytics(BrandCategoryModal)
